import usePersistedState from '@hooks/usePersistedState';
import React, { createContext, useCallback, useState, useMemo, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';

import { isBrowser } from '@utils/isBrowser';
import { logout } from '@utils/auth';
import { destroyWebPushToken, unregisterServiceWorker } from '@utils/pushNotification';
import { destroyTour } from '@utils/webTour';
import useCurrentUser from '@hooks/useCurrentUser';
import { ROUTE_PAYMENT_AND_DEBT } from '../utils/routeConstants';
import { PERMISSION_VIEW_DEBT_AND_PAYMENT } from '../utils/constants';

type ContextValue = {
  isLoggedIn: boolean;
  handleLogout: () => void;
  handleRemoveTokenAndService: () => void;
};

const AuthContext: React.Context<ContextValue> = createContext<ContextValue>({
  isLoggedIn: false,
  handleLogout: () => null,
  handleRemoveTokenAndService: () => null,
});

const env = process.env.NEXT_APP_ENV as 'dev' | 'staging' | 'production';

export default function AuthController({ children }: { children: React.ReactNode }) {
  const [authData] = usePersistedState('auth', null);
  const [isLoggedIn, setLoggedIn] = useState(Boolean(authData));
  const router = useRouter();

  const { checkPermissionWithEnv } = useCurrentUser();

  const toHome = useCallback(() => {
    if (isBrowser) window.location.href = '/';
    else router.push('/');
  }, []);

  const handleLogout = useCallback(() => {
    handleRemoveTokenAndService();
    toHome();
  }, []);

  const handleRemoveTokenAndService = () => {
    setLoggedIn(false);
    logout();

    // Handle web push notification when logout
    unregisterServiceWorker();
    destroyWebPushToken();

    // Handle web tour when logout
    destroyTour();
  };

  useEffect(() => {
    if (env === 'production') return;

    if (router.pathname === ROUTE_PAYMENT_AND_DEBT && !checkPermissionWithEnv?.(PERMISSION_VIEW_DEBT_AND_PAYMENT)) {
      router.push('/');
    }
  }, [router.pathname]);

  const context: ContextValue = useMemo(
    () => ({
      isLoggedIn,
      handleLogout,
      handleRemoveTokenAndService,
    }),
    [isLoggedIn, handleLogout, handleRemoveTokenAndService],
  );

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
}

export function useAuth(): ContextValue {
  return useContext<ContextValue>(AuthContext);
}
